import classNames from 'classnames';
import { ComponentProps } from 'react';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import Button from '../Button';
import IllustrationRenderer, { IllustrationDefinition } from '../IllustrationRenderer';
import styles from './styles/styles.module.scss';

export type ButtonDefinition = Pick<ComponentProps<typeof Button>, 'icon' | 'linkInfo' | 'onClick' | 'text'>;

type NullStateProps = {
  buttons?: { primary?: ButtonDefinition | undefined; flat?: ButtonDefinition | undefined; default?: ButtonDefinition | undefined };
  className?: string;
  description?: string;
  descriptionClassName?: string;
  illustration?: IllustrationDefinition<never>; // If you are thinking about adding size or color, make sure that the usage can't be satisfied using the `size` or `inverted` props
  inverted?: boolean;
  noTopMargin?: boolean;
  title: string;
  titleClassName?: string;
};

export default function NullState({
  buttons,
  className,
  description,
  descriptionClassName,
  illustration,
  inverted,
  noTopMargin = false,
  title,
  titleClassName
}: NullStateProps) {
  const buttonDefinitions: { variant: ComponentProps<typeof Button>['variant']; button?: ButtonDefinition }[] = [
    { variant: 'primary', button: buttons?.primary },
    { variant: inverted ? 'inverse' : 'ghost', button: buttons?.flat },
    { variant: 'secondary', button: buttons?.default }
  ];
  const renderedButtons = buttonDefinitions
    .filter(({ button }) => !!button)
    .map(({ variant, button }) => <Button key={`null-state-${variant}-button`} {...(button as ButtonDefinition)} variant={variant} width="auto" />);

  return (
    <div {...setDenaliAttribute('NullState')} className={classNames(styles.container, { [styles.noTopMargin]: noTopMargin }, className)}>
      {illustration && (
        <div className={styles.illustration}>
          <IllustrationRenderer
            illustration={illustration}
            defaults={{ color: inverted ? 'var(--color-text-primary-inverse)' : 'var(--color-text-primary)', size: 'sm' }}
          />
        </div>
      )}
      <h2 className={classNames(styles.title, { [styles.inverted]: inverted }, titleClassName)}>{title}</h2>
      {description && <div className={classNames(styles.description, { [styles.inverted]: inverted }, descriptionClassName)}>{description}</div>}
      {renderedButtons.length > 0 && <div className={styles.buttons}>{renderedButtons}</div>}
    </div>
  );
}
